import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import PageHeader from '../components/layout/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle2, XCircle, ArrowLeft } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

export default function WorkflowStatus() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [status, setStatus] = useState<'running' | 'completed' | 'failed'>('running');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!runId) {
      navigate('/dashboard');
      return;
    }

    // Poll workflow status every second
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`/api/workflows/${runId}/status`);
        if (!response.ok) {
          throw new Error('Failed to fetch workflow status');
        }
        const data = await response.json();
        
        setStatus(data.status);
        setProgress(data.progress || 0);
        setError(data.error);

        // If completed or failed, stop polling
        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(interval);
          
          if (data.status === 'completed') {
            toast({
              title: 'Workflow completed',
              description: 'Market research workflow finished successfully',
            });
            // Redirect to dashboard after 2 seconds
            setTimeout(() => {
              navigate('/dashboard');
            }, 2000);
          }
        }
      } catch (err: any) {
        console.error('Error fetching workflow status:', err);
        // Don't stop polling on network errors, just log them
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [runId, navigate, toast]);

  return (
    <DashboardLayout>
      <PageHeader
        title="Workflow Status"
        description={`Tracking workflow execution: ${runId}`}
        action={
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
        }
      />

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>Market Research Workflow</CardTitle>
          <CardDescription>
            Workflow ID: {runId}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status Indicator */}
          <div className="flex items-center gap-4">
            {status === 'running' && (
              <>
                <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                <div>
                  <p className="font-medium">Workflow Running</p>
                  <p className="text-sm text-muted-foreground">
                    Processing market research data...
                  </p>
                </div>
              </>
            )}
            {status === 'completed' && (
              <>
                <CheckCircle2 className="h-6 w-6 text-green-500" />
                <div>
                  <p className="font-medium">Workflow Completed</p>
                  <p className="text-sm text-muted-foreground">
                    Market research data has been processed successfully
                  </p>
                </div>
              </>
            )}
            {status === 'failed' && (
              <>
                <XCircle className="h-6 w-6 text-red-500" />
                <div>
                  <p className="font-medium">Workflow Failed</p>
                  <p className="text-sm text-muted-foreground">
                    {error || 'An error occurred during workflow execution'}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Progress Bar */}
          {status === 'running' && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progress</span>
                <span>{progress}%</span>
              </div>
              <Progress value={progress} />
            </div>
          )}

          {/* Error Alert */}
          {status === 'failed' && error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Success Message */}
          {status === 'completed' && (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                Workflow completed successfully. Redirecting to dashboard...
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}

